import {
    FormRef,
    IressButton,
    IressForm,
    IressFormField,
    IressInline,
    IressInput,
    IressModal,
    IressSkeleton,
    IressStack,
    useModal,
} from '@iress-oss/ids-components';
import { FC, RefObject, useRef } from 'react';
import { IressFormDataType, Nullable } from '@xplan-pension-switching/common';
import { useSearchParams } from 'react-router';
import { useScenarioQuery, useScenarioQueryInvalidation } from '../../services';

export const EditScenarioModal: FC<{ container?: RefObject<Nullable<HTMLDivElement>> }> = ({ container }) => {
    const formRef = useRef<FormRef<IressFormDataType>>(null);
    const { showModal } = useModal();
    const [urlParams, setUrlParams] = useSearchParams();
    const entityId = urlParams.get('entityid');
    const scenarioId = urlParams.get('editScenarioId');
    const { data } = useScenarioQuery(Number(entityId), Number(scenarioId));
    const invalidateQueries = useScenarioQueryInvalidation(Number(entityId));
    return (
        <IressModal
            container={container}
            id="edit-scenario-modal"
            heading="Edit Scenario"
            data-testid="edit-scenario-modal"
            size="sm"
            onExited={() => {
                urlParams.delete('editScenarioId');
                setUrlParams(urlParams);
            }}
            footer={
                data?.scenarioName ? (
                    <IressInline horizontalAlign="between">
                        <IressButton
                            mode="tertiary"
                            onClick={() => {
                                urlParams.set('deleteScenarioId', scenarioId!);
                                setUrlParams(urlParams);
                                showModal('delete-scenario-modal', true);
                                showModal('edit-scenario-modal', false);
                            }}
                            data-testid="delete-scenario-button"
                        >
                            Delete scenario
                        </IressButton>
                        <IressInline gutter="sm">
                            <IressButton
                                mode="secondary"
                                onClick={() => {
                                    showModal('edit-scenario-modal', false);
                                }}
                                data-testid="cancel-scenario-button"
                            >
                                Cancel
                            </IressButton>
                            <IressButton
                                mode="primary"
                                data-testid="save-scenario-button"
                                onClick={() => formRef.current?.submit()}
                            >
                                Save
                            </IressButton>
                        </IressInline>
                    </IressInline>
                ) : (
                    <IressSkeleton textVariant="display3" width="100%" data-testid="buttons-loading-placeholder" />
                )
            }
        >
            {data?.scenarioName ? (
                <IressForm
                    ref={formRef}
                    shouldUnregister
                    mode="onBlur"
                    hiddenErrorSummary
                    onSubmit={_values => {
                        showModal('edit-scenario-modal', false);
                        invalidateQueries();
                    }}
                >
                    <IressFormField
                        label="Scenario name"
                        name="scenarioName"
                        defaultValue={data?.scenarioName}
                        rules={{ required: true }}
                        render={controlledProps => (
                            <IressInput {...controlledProps} data-testid="scenario-name-field" />
                        )}
                    />
                </IressForm>
            ) : (
                <IressStack gutter="sm">
                    <IressSkeleton
                        mode="text"
                        textVariant="display4"
                        width="33%"
                        data-testid="label-loading-placeholder"
                    />
                    <IressSkeleton textVariant="display3" width="100%" data-testid="input-loading-placeholder" />
                </IressStack>
            )}
        </IressModal>
    );
};
